// src/pages/OrderSettingsPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { faUserEdit, faTrash, faKey, faUserPlus, faCircleInfo, faBan, faWarning, faEye, faList, faCircleCheck, faEyeSlash, faEnvelope, faEdit, faCodeMerge, faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import {
    Container,
    Card,
    Row,
    Col,
    Form,
    Button,
    Table,
    Alert,
    Spinner,
    Badge,
    Modal,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

// Redux slices
import {
    fetchOrderSettings,
    updateOrderSettings,
} from "../redux/slice/orderSettingsSlice";

import {
    fetchUserDescriptions,
    createUserDescription,
    updateUserDescription,
    deleteUserDescription,
    mergeUserDescriptions,
} from "../redux/slice/userDescriptionsSlice";

import NotificationToast from "../components/Notifications";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const OrderSettingsPage = () => {
    const dispatch = useDispatch();

    // Redux state
    const {
        percentages,
        rules,
        status,
        error,
        saveStatus,
        saveError,
    } = useSelector((state) => state.orderSettings);

    const { items: descriptions, status: descStatus, error: descError } = useSelector(
        (state) => state.userDescriptions
    );

    // Local state
    const [localPercentages, setLocalPercentages] = useState({ A: 60, B: 40 });
    const [localRules, setLocalRules] = useState([]);

    // New rule state
    const [newDescriptionId, setNewDescriptionId] = useState("");
    const [newGroup, setNewGroup] = useState("A");

    // UI state
    const [message, setMessage] = useState(null);
    const [toast, setToast] = useState({
        show: false,
        message: "",
        variant: "info",
    });
    const [activeTab, setActiveTab] = useState("settings");

    // Modals for description CRUD
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newDescName, setNewDescName] = useState("");
    const [showConfirmSave, setShowConfirmSave] = useState(false);

    const [showRenameModal, setShowRenameModal] = useState(false);
    const [renameId, setRenameId] = useState(null);
    const [renameValue, setRenameValue] = useState("");

    const [showMergeModal, setShowMergeModal] = useState(false);
    const [mergeFromId, setMergeFromId] = useState("");
    const [mergeToId, setMergeToId] = useState("");

    const [showMergeConfirmModal, setShowMergeConfirmModal] = useState(false);
    const [mergeConfirmed, setMergeConfirmed] = useState(false);


    const isAdminDescription = (desc) =>
        desc.name?.toLowerCase() === "admin";

    const showToast = (msg, variant = "info") => {
        setToast({ show: true, message: msg, variant });
    };

    // Fetch backend data on first load
    useEffect(() => {
        dispatch(fetchOrderSettings());
        dispatch(fetchUserDescriptions());
    }, [dispatch]);

    // Sync backend → local state
    useEffect(() => {
        if (status === "succeeded") {
            setLocalPercentages({
                A: Math.round((percentages.A || 0) * 100),
                B: Math.round((percentages.B || 0) * 100),
            });

            if (rules?.length) {
                setLocalRules(
                    rules.map((r) => ({
                        description_id: r.description_id,
                        description_name: r.description_name,
                        group: r.group,
                    }))
                );
            }
        }
    }, [status, percentages, rules]);

    const availableDescriptions = useMemo(() => {
        const usedIds = new Set(
            localRules.map(r => r.description_id)
        );

        return descriptions.filter(d =>
            !usedIds.has(d.id) &&
            d.name?.toLowerCase() !== "admin"
        );
    }, [descriptions, localRules]);

    useEffect(() => {
        if (
            newDescriptionId &&
            !availableDescriptions.some(
                d => d.id === Number(newDescriptionId)
            )
        ) {
            setNewDescriptionId("");
        }
    }, [availableDescriptions, newDescriptionId]);


    // Count rules per group
    const { countA, countB } = useMemo(() => {
        let a = 0,
            b = 0;
        localRules.forEach((r) => (r.group === "A" ? a++ : b++));
        return { countA: a, countB: b };
    }, [localRules]);

    // Handle percentage slider change
    const handlePercentChange = (value) => {
        let aVal = Number(value);
        if (Number.isNaN(aVal)) aVal = 0;
        if (aVal < 0) aVal = 0;
        if (aVal > 100) aVal = 100;

        setLocalPercentages({ A: aVal, B: 100 - aVal });
    };

    const hasUnsavedChanges = useMemo(() => {
        // percentages
        const backendPercentages = {
            A: Math.round((percentages.A || 0) * 100),
            B: Math.round((percentages.B || 0) * 100),
        };

        if (
            backendPercentages.A !== localPercentages.A ||
            backendPercentages.B !== localPercentages.B
        ) {
            return true;
        }

        // rules (order-independent)
        const normalize = (arr) =>
            arr
                .map(r => `${r.description_id}-${r.group}`)
                .sort()
                .join("|");

        return normalize(rules || []) !== normalize(localRules);
    }, [percentages, rules, localPercentages, localRules]);


    // ADD RULE
    const handleAddRule = () => {
        if (!newDescriptionId) {
            setMessage("Επιλέξτε μια ιδιότητα.");
            return;
        }

        const existing = localRules.some(
            (r) => r.description_id === Number(newDescriptionId)
        );
        if (existing) {
            setMessage("Αυτή η ιδιότητα υπάρχει ήδη.");
            return;
        }

        const selected = descriptions.find(
            (d) => d.id === Number(newDescriptionId)
        );
        if (!selected) {
            setMessage("Άκυρη ιδιότητα.");
            return;
        }

        setLocalRules((prev) => [
            ...prev,
            {
                description_id: selected.id,
                description_name: selected.name,
                group: newGroup,
            },
        ]);

        setNewDescriptionId("");
        setMessage(null);
    };

    // TOGGLE GROUP
    const handleToggleRuleGroup = (index) => {
        setLocalRules((prev) =>
            prev.map((r, i) =>
                i === index ? { ...r, group: r.group === "A" ? "B" : "A" } : r
            )
        );
    };

    // Remove rule
    const handleRemoveRule = (index) => {
        setLocalRules((prev) => prev.filter((_, i) => i !== index));
    };

    // SAVE SETTINGS
    const handleSave = () => {
        const payload = {
            percentages: {
                A: localPercentages.A / 100,
                B: localPercentages.B / 100,
            },
            rules: localRules.map((r) => ({
                description_id: r.description_id,
                group: r.group,
            })),
        };

        dispatch(updateOrderSettings(payload))
            .unwrap()
            .then(() => {
                showToast("Οι ρυθμίσεις αποθηκεύτηκαν επιτυχώς.", "success");
            })
            .catch(() => {
                showToast("Αποτυχία αποθήκευσης.", "danger");
            });
    };

    const isSettingsLoading = status === "loading" || descStatus === "loading";
    const isDescriptionsLoading = descStatus === "loading";

    const openRenameModal = (desc) => {
        setRenameId(desc.id);
        setRenameValue(desc.name);
        setShowRenameModal(true);
    };

    const handleDeleteDescription = (desc) => {
        if (
            !window.confirm(
                `Θέλετε σίγουρα να διαγράψετε την ιδιότητα "${desc.name}";`
            )
        ) {
            return;
        }

        dispatch(deleteUserDescription(desc.id))
            .unwrap()
            .then(() => {
                setLocalRules((prev) =>
                    prev.filter((rule) => rule.description_id !== desc.id)
                );
                showToast("Η ιδιότητα διαγράφηκε.", "success");
                dispatch(fetchOrderSettings());
            })
            .catch(() => {
                showToast(
                    "Αποτυχία διαγραφής. Ελέγξτε αν η ιδιότητα χρησιμοποιείται.",
                    "danger"
                );
            });
    };

    // CREATE NEW DESCRIPTION
    const submitCreateDescription = () => {
        if (!newDescName.trim()) {
            showToast("Το όνομα δεν μπορεί να είναι κενό.", "warning");
            return;
        }

        dispatch(createUserDescription({ name: newDescName.trim() }))
            .unwrap()
            .then(() => {
                setShowCreateModal(false);
                setNewDescName("");
                showToast("Η ιδιότητα δημιουργήθηκε.", "success");
            })
            .catch(() => {
                showToast("Αποτυχία δημιουργίας.", "danger");
            });
    };

    // RENAME DESCRIPTION
    const submitRenameDescription = () => {
        if (!renameValue.trim()) {
            showToast("Το όνομα δεν μπορεί να είναι κενό.", "warning");
            return;
        }

        dispatch(updateUserDescription({ id: renameId, name: renameValue.trim() }))
            .unwrap()
            .then(() => {
                setShowRenameModal(false);
                setLocalRules((prev) =>
                    prev.map((rule) =>
                        rule.description_id === renameId
                            ? { ...rule, description_name: renameValue.trim() }
                            : rule
                    )
                );
                showToast("Η ιδιότητα ενημερώθηκε.", "success");
            })
            .catch(() => {
                showToast("Αποτυχία ενημέρωσης.", "danger");
            });
    };

    // MERGE DESCRIPTIONS
    const submitMergeDescriptions = () => {
        if (!mergeFromId || !mergeToId) {
            showToast("Επιλέξτε και τις δύο ιδιότητες.", "warning");
            return;
        }
        if (mergeFromId === mergeToId) {
            showToast("Οι ιδιότητες πρέπει να είναι διαφορετικές.", "warning");
            return;
        }

        dispatch(
            mergeUserDescriptions({
                from_id: Number(mergeFromId),
                to_id: Number(mergeToId),
            })
        )
            .unwrap()
            .then(() => {
                setShowMergeModal(false);
                showToast("Η συνένωση ολοκληρώθηκε.", "success");
                dispatch(fetchUserDescriptions());
                dispatch(fetchOrderSettings());
            })
            .catch(() => {
                showToast("Αποτυχία συνένωσης.", "danger");
            });
    };

    return (
        <Container className="mt-4" style={{ minHeight: "100vh" }}>
            <Card className="shadow-sm" style={{ borderRadius: 14 }}>
                <Card.Header style={{ background: "white" }}>
                    <h3 style={{ fontWeight: 600 }}>Ρυθμίσεις Κατανομής Γευμάτων</h3>
                    <p style={{ fontSize: 14, color: "gray" }}>
                        Ορισμός ποσοστών και ιδιοτήτων χρηστών για Group A / B.
                    </p>
                </Card.Header>

                <div
                    style={{
                        display: "flex",
                        background: "#f1f3f5",
                        padding: 5,
                        borderRadius: 14,
                        margin: "20px 20px 0 20px",
                    }}
                >
                    <div
                        onClick={() => setActiveTab("settings")}
                        style={{
                            flex: 1,
                            padding: "12px 0",
                            textAlign: "center",
                            fontWeight: 600,
                            cursor: "pointer",
                            borderRadius: 12,
                            background: activeTab === "settings" ? "white" : "transparent",
                            boxShadow:
                                activeTab === "settings" ? "0 2px 6px rgba(0,0,0,0.08)" : "none",
                            transition: "0.2s",
                        }}
                    >
                        Κατανομή Παραγγελιών
                    </div>

                    <div
                        onClick={() => setActiveTab("descriptions")}
                        style={{
                            flex: 1,
                            padding: "12px 0",
                            textAlign: "center",
                            fontWeight: 600,
                            cursor: "pointer",
                            borderRadius: 12,
                            background: activeTab === "descriptions" ? "white" : "transparent",
                            boxShadow:
                                activeTab === "descriptions"
                                    ? "0 2px 6px rgba(0,0,0,0.08)"
                                    : "none",
                            transition: "0.2s",
                        }}
                    >
                        Ιδιότητες Χρηστών
                    </div>
                </div>

                <Card.Body>
                    {activeTab === "settings" && (
                        <>
                            {hasUnsavedChanges && (
                                <Alert variant="warning" className="mt-3">
                                    Έχετε κάνει αλλαγές. Οι αλλαγές <strong>δεν θα εφαρμοστούν</strong> αν δεν
                                    πατήσετε <strong>Αποθήκευση</strong>.
                                </Alert>
                            )}
                            {message && <Alert variant="info">{message}</Alert>}
                            {error && <Alert variant="danger">{error}</Alert>}
                            {saveError && <Alert variant="danger">{saveError}</Alert>}

                            {isSettingsLoading ? (
                                <div className="text-center my-5">
                                    <Spinner animation="border" />
                                </div>
                            ) : (
                                <>
                                    <Row className="mb-4">
                                        <Col md={6}>
                                            <Card className="p-3 shadow-sm">
                                                <h5 style={{ fontWeight: 600 }}>Ποσοστά Κατανομής</h5>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>
                                                        Group A (%)
                                                        <Badge bg="primary" pill className="ms-2">
                                                            Κύριοι χρήστες
                                                        </Badge>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        inputMode="numeric"
                                                        pattern="[0-9]*"
                                                        // type="number"
                                                        // min={0}
                                                        // max={100}
                                                        value={localPercentages.A}
                                                        onChange={(e) =>
                                                            handlePercentChange(e.target.value)
                                                        }
                                                    />
                                                </Form.Group>

                                                <Form.Group>
                                                    <Form.Label>Group B (%)</Form.Label>
                                                    <Form.Control value={localPercentages.B} readOnly />
                                                </Form.Group>
                                            </Card>
                                        </Col>

                                        <Col md={6}>
                                            <Card className="p-3 shadow-sm">
                                                <h5 style={{ fontWeight: 600 }}>Σύνοψη</h5>
                                                <p>
                                                    <strong>Group A:</strong> {countA} ιδιότητες
                                                </p>
                                                <p>
                                                    <strong>Group B:</strong> {countB} ιδιότητες
                                                </p>

                                                {/* <Button
                                                    variant="secondary"
                                                    className="mt-3"
                                                    onClick={() => setShowCreateModal(true)}
                                                >
                                                    + Νέα Ιδιότητα
                                                </Button> */}

                                                {/* <Button
                                                    variant="warning"
                                                    className="mt-3 ms-2"
                                                    onClick={() => setShowMergeModal(true)}
                                                >
                                                    Συγχώνευση Ιδιοτήτων
                                                </Button> */}
                                            </Card>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={12}>
                                            <h5 style={{ fontWeight: 600 }}>
                                                Ιδιότητες χρηστών ανά Group
                                            </h5>

                                            <Row className="mb-3">
                                                <Col md={7}>
                                                    <Form.Select
                                                        value={newDescriptionId}
                                                        onChange={(e) => setNewDescriptionId(e.target.value)}
                                                    >
                                                        <option value="">Επιλέξτε Ιδιότητα</option>

                                                        {availableDescriptions.map((d) => (
                                                            <option key={d.id} value={d.id}>
                                                                {d.name}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Col>

                                                <Col md={3}>
                                                    <Form.Select
                                                        value={newGroup}
                                                        onChange={(e) => setNewGroup(e.target.value)}
                                                    >
                                                        <option value="A">Group A</option>
                                                        <option value="B">Group B</option>
                                                    </Form.Select>
                                                </Col>

                                                <Col md={2}>
                                                    <Button
                                                        className="btn-add"
                                                        style={{ width: "100%" }}
                                                        onClick={handleAddRule}
                                                    >
                                                        Προσθήκη
                                                    </Button>
                                                </Col>
                                            </Row>

                                            {localRules.length === 0 ? (
                                                <Alert variant="warning" className="mt-3">
                                                    Δεν υπάρχουν ορισμένες ιδιότητες.
                                                </Alert>
                                            ) : (
                                                <Table bordered hover responsive className="shadow-sm">
                                                    <thead>
                                                        <tr>
                                                            <th>Ιδιότητα</th>
                                                            <th style={{ width: 180 }}>Group</th>
                                                            {/* <th style={{ width: 200 }}>Ενέργειες</th> */}
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {localRules.map((rule, index) => (
                                                            <tr key={index}>
                                                                <td>{rule.description_name}</td>

                                                                <td style={{ textAlign: "center" }}>
                                                                    <Button
                                                                        size="sm"
                                                                        variant={
                                                                            rule.group === "A"
                                                                                ? "primary"
                                                                                : "outline-secondary"
                                                                        }
                                                                        onClick={() =>
                                                                            handleToggleRuleGroup(index)
                                                                        }
                                                                    >
                                                                        <strong>{rule.group}</strong>
                                                                    </Button>
                                                                </td>

                                                                {/* <td style={{ textAlign: "center" }}>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="danger"
                                                                        onClick={() =>
                                                                            handleRemoveRule(index)
                                                                        }
                                                                    >
                                                                        Διαγραφή
                                                                    </Button>
                                                                </td> */}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            )}
                                        </Col>
                                    </Row>

                                    <Row className="mt-4">
                                        <Col className="d-flex justify-content-end">

                                            <Button
                                                className="btn-add"
                                                disabled={!hasUnsavedChanges || saveStatus === "loading"}
                                                onClick={() => setShowConfirmSave(true)}
                                            >
                                                {saveStatus === "loading" ? <Spinner size="sm" /> : "Αποθήκευση Ρυθμίσεων"}
                                            </Button>

                                        </Col>
                                    </Row>
                                </>
                            )}
                        </>
                    )}

                    {activeTab === "descriptions" && (
                        <>
                            {descError && <Alert variant="danger">{descError}</Alert>}

                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3">
                                <div>
                                    <h5 style={{ fontWeight: 600 }}>Διαχείριση Ιδιοτήτων Χρηστών</h5>
                                    <p style={{ color: "gray", marginBottom: 0 }}>
                                        Δημιουργία, μετονομασία, διαγραφή ή συγχώνευση ιδιοτήτων.
                                    </p>
                                </div>
                                <div className="mt-3 mt-md-0">
                                    <Button
                                        className="btn-email"
                                        variant="primary"
                                        onClick={() => setShowCreateModal(true)}
                                    >
                                        <FontAwesomeIcon icon={faUserPlus} className="me-2" /><strong>Ιδιότητα</strong>
                                    </Button>
                                    <Button
                                        className="btn-edits"
                                        // className="ms-2"
                                        onClick={() => setShowMergeModal(true)}
                                    >
                                        <FontAwesomeIcon icon={faArrowRight} className="me-2" /><FontAwesomeIcon icon={faArrowLeft} className="me-2" /><strong>Συγχώνευση</strong>
                                    </Button>
                                </div>
                            </div>

                            {isDescriptionsLoading ? (
                                <div className="text-center my-5">
                                    <Spinner animation="border" />
                                </div>
                            ) : descriptions.length === 0 ? (
                                <Alert variant="warning">
                                    Δεν υπάρχουν καταχωρημένες ιδιότητες χρηστών.
                                </Alert>
                            ) : (
                                <Table bordered hover responsive className="shadow-sm">
                                    <thead>
                                        <tr>
                                            <th>Ιδιότητα</th>
                                            <th style={{ width: 220 }}>Ενέργειες</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {descriptions.map((desc) => {
                                            const isAdmin = isAdminDescription(desc);

                                            return (
                                                <tr key={desc.id}>
                                                    <td>
                                                        {desc.name}
                                                        {isAdmin && (
                                                            <Badge bg="secondary" className="ms-2">
                                                                system
                                                            </Badge>
                                                        )}
                                                    </td>
                                                    <td style={{ display: 'flex', justifyContent: 'center' }}>
                                                        <Button
                                                            className="btn-edit"
                                                            size="sm"
                                                            disabled={isAdmin}
                                                            onClick={() => openRenameModal(desc)}
                                                        >
                                                            <FontAwesomeIcon icon={faEdit} style={{ padding: '2px' }} />
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="danger"
                                                            className="ms-2"
                                                            disabled={isAdmin}
                                                            onClick={() => handleDeleteDescription(desc)}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} style={{ padding: '2px' }} />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}

                                    </tbody>
                                </Table>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Notifications */}
            <NotificationToast
                show={toast.show}
                message={toast.message}
                variant={toast.variant}
                onClose={() => setToast({ ...toast, show: false })}
            />

            {/* -------------------- CREATE DESCRIPTION MODAL -------------------- */}
            <Modal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Νέα Ιδιότητα</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Όνομα Ιδιότητας</Form.Label>
                        <Form.Control
                            value={newDescName}
                            onChange={(e) => setNewDescName(e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                        Άκυρο
                    </Button>
                    <Button variant="primary" onClick={submitCreateDescription}>
                        Αποθήκευση
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* -------------------- RENAME DESCRIPTION MODAL -------------------- */}
            <Modal
                show={showRenameModal}
                onHide={() => setShowRenameModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Μετονομασία Ιδιότητας</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Νέο Όνομα</Form.Label>
                        <Form.Control
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRenameModal(false)}>
                        Άκυρο
                    </Button>
                    <Button variant="primary" onClick={submitRenameDescription}>
                        Αποθήκευση
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* -------------------- MERGE DESCRIPTION MODAL -------------------- */}
            <Modal
                show={showMergeModal}
                onHide={() => setShowMergeModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Συγχώνευση Ιδιοτήτων</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <p>
                        Όλοι οι χρήστες της "παλιάς" ιδιότητας θα μεταφερθούν στην "νέα"
                        ιδιότητα και η παλιά θα διαγραφεί.
                    </p>

                    <Form.Group className="mb-3">
                        <Form.Label>Ιδιότητα προς Διαγραφή</Form.Label>
                        <Form.Select
                            value={mergeFromId}
                            onChange={(e) => setMergeFromId(e.target.value)}
                        >
                            <option value="">Επιλέξτε</option>
                            {descriptions
                                .filter(d => d.name?.toLowerCase() !== "admin")
                                .map((d) => (
                                    <option key={d.id} value={d.id}>
                                        {d.name}
                                    </option>
                                ))}

                        </Form.Select>
                    </Form.Group>

                    <Form.Group>
                        <Form.Label>Μεταφορά σε Ιδιότητα</Form.Label>
                        <Form.Select
                            value={mergeToId}
                            onChange={(e) => setMergeToId(e.target.value)}
                        >
                            <option value="">Επιλέξτε</option>
                            {descriptions
                                .filter(d => d.name?.toLowerCase() !== "admin")
                                .map((d) => (
                                    <option key={d.id} value={d.id}>
                                        {d.name}
                                    </option>
                                ))}
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowMergeModal(false)}>
                        Άκυρο
                    </Button>
                    <Button
                        variant="primary"
                        disabled={!mergeFromId || !mergeToId || mergeFromId === mergeToId}
                        onClick={() => setShowMergeConfirmModal(true)}
                    >
                        Συγχώνευση
                    </Button>

                </Modal.Footer>
            </Modal>
            <Modal
                show={showConfirmSave}
                onHide={() => setShowConfirmSave(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Επιβεβαίωση Αλλαγών</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <p>
                        Έχετε πραγματοποιήσει αλλαγές στις ρυθμίσεις κατανομής.
                    </p>

                    <p>
                        Θέλετε να αποθηκευτούν και να εφαρμοστούν άμεσα;
                    </p>

                    <hr />

                    <p style={{ fontSize: 14, color: "#6c757d" }}>
                        <strong>Σημείωση:</strong> Οι αλλαγές αυτές θα ισχύσουν αποκλειστικά για
                        προγράμματα που θα δημιουργηθούν από εδώ και στο εξής.
                        <br />
                        Τα ήδη καταχωρημένα προγράμματα δεν επηρεάζονται και θα συνεχίσουν
                        να εφαρμόζουν την προηγούμενη ποσοστιαία κατανομή και τον
                        υφιστάμενο διαχωρισμό Group A / B.
                    </p>
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowConfirmSave(false)}
                    >
                        Άκυρο
                    </Button>

                    <Button
                        variant="primary"
                        onClick={() => {
                            setShowConfirmSave(false);
                            handleSave();
                        }}
                    >
                        Επιβεβαίωση & Αποθήκευση
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={showMergeConfirmModal}
                onHide={() => {
                    setShowMergeConfirmModal(false);
                    setMergeConfirmed(false);
                }}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title className="text-danger">
                        <FontAwesomeIcon icon={faWarning} className="me-2" />
                        Μη Αναστρέψιμη Ενέργεια
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Alert variant="danger">
                        <strong>ΠΡΟΣΟΧΗ:</strong> Η συγχώνευση ιδιοτήτων είναι <u>οριστική</u>.
                        <br />
                        Η ενέργεια αυτή <strong>δεν μπορεί να αναιρεθεί</strong>.
                    </Alert>

                    <p>
                        Η ιδιότητα:
                        <strong className="ms-1">
                            {descriptions.find(d => d.id === Number(mergeFromId))?.name}
                        </strong>
                        <br />
                        θα <strong>διαγραφεί οριστικά</strong> και:
                    </p>

                    <ul>
                        <li>Όλοι οι χρήστες θα μεταφερθούν</li>
                        <li>Όλα τα rules θα ενημερωθούν</li>
                        <li>Τα υπάρχοντα snapshots <strong>δεν αλλάζουν</strong></li>
                    </ul>

                    <Form.Check
                        type="checkbox"
                        className="mt-3"
                        label="Κατανοώ ότι αυτή η ενέργεια δεν μπορεί να αναιρεθεί"
                        checked={mergeConfirmed}
                        onChange={(e) => setMergeConfirmed(e.target.checked)}
                    />
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setShowMergeConfirmModal(false);
                            setMergeConfirmed(false);
                        }}
                    >
                        Άκυρο
                    </Button>

                    <Button
                        variant="danger"
                        disabled={!mergeConfirmed}
                        onClick={() => {
                            setShowMergeConfirmModal(false);
                            setMergeConfirmed(false);
                            submitMergeDescriptions();
                        }}
                    >
                        Ναι, Συγχώνευση Οριστικά
                    </Button>
                </Modal.Footer>
            </Modal>


        </Container>
    );
};

export default OrderSettingsPage;
